import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/common/SEO';

export default function Itineraries() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "url": "https://la-antigua-guatemala.com/itineraries",
    "mainEntityOfPage": "https://la-antigua-guatemala.com/itineraries",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How many days are enough in Antigua Guatemala?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We recommend 3 days minimum for first-time visitors to Antigua Guatemala. This allows time to explore the colonial city center, complete the iconic Acatenango overnight volcano hike, and take one day trip. If you have 5-7 days, you can add Lake Atitlán, coffee tours, and Chichicastenango market."
        }
      },
      {
        "@type": "Question",
        "name": "Is 2 days enough in Antigua Guatemala?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Two days is enough to see Antigua's main colonial highlights and hike Pacaya volcano, but you'll miss the spectacular Acatenango overnight experience. A 2-day itinerary works for US travelers on a long weekend, but we recommend 3 days minimum if possible."
        }
      },
      {
        "@type": "Question",
        "name": "Is 3 days too much for Antigua Guatemala?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No, 3 days is the ideal duration for Antigua Guatemala. This gives you time to explore the city without rushing, complete the Acatenango overnight hike, and still have energy to enjoy the restaurants and cafes. Most first-time visitors find 3 days perfect."
        }
      },
      {
        "@type": "Question",
        "name": "What is the best itinerary for first-time visitors to Antigua?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The best first-time itinerary is 3 days: Day 1 - Explore colonial Antigua (Santa Catalina Arch, Central Park, ruins, markets). Day 2-3 - Acatenango overnight volcano hike to watch Fuego erupt. This covers the essential experiences without feeling rushed."
        }
      },
      {
        "@type": "Question",
        "name": "Can you visit Lake Atitlán from Antigua Guatemala?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, Lake Atitlán is a 2.5-3 hour drive from Antigua. You can visit as a long day trip, but we recommend staying 2-3 nights at the lake. A 7-day itinerary combining Antigua (3-4 days) and Lake Atitlán (3 days) is ideal for experiencing both destinations properly."
        }
      },
      {
        "@type": "Question",
        "name": "Is Antigua Guatemala worth 5 days?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, 5 days in Antigua allows you to explore at a relaxed pace with multiple day trips. You can hike Acatenango, visit Chichicastenango market, tour coffee plantations, explore Iximché ruins, and still have time to enjoy the city's restaurants, cafes, and nightlife without rushing."
        }
      },
      {
        "@type": "Question",
        "name": "What should I prioritize if I have limited time in Antigua?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "With limited time, prioritize: 1) Walking tour of colonial Antigua (Santa Catalina Arch, Central Park, La Merced Church), 2) One volcano hike (Pacaya for 2 days, Acatenango for 3+ days), 3) Sunset at a rooftop bar. Skip distant day trips and focus on the city and one volcano experience."
        }
      },
      {
        "@type": "Question",
        "name": "When is the best time to visit Antigua Guatemala?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The best time to visit Antigua is during the dry season (November to April), with December-February offering the clearest volcano views. March-April brings Semana Santa (Holy Week) celebrations. The rainy season (May-October) has afternoon showers but fewer crowds and lower prices."
        }
      }
    ]
  };

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://la-antigua-guatemala.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Itineraries",
        "item": "https://la-antigua-guatemala.com/itineraries"
      }
    ]
  };

  // Combine schemas into array
  const schemas = [pageSchema, faqSchema];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Antigua Guatemala Itinerary (2, 3, 5 & 7 Days) — Best Plans (2026)"
        description="Antigua Guatemala itinerary (2026): 2, 3, 5 & 7-day day-by-day plans with Acatenango hike options, Lake Atitlán combos, maps, and US traveler tips."
        canonical="https://la-antigua-guatemala.com/itineraries"
        keywords="antigua guatemala itinerary, antigua guatemala itineraries, 2 day antigua itinerary, 3 day antigua guatemala itinerary, 5 day antigua itinerary, how many days in antigua guatemala, antigua trip plan, antigua weekend itinerary, antigua guatemala travel plan"
        schema={schemas}
        lastModified="2026-02-20"
        ogImage={{
          url: "https://la-antigua-guatemala.com/og/itineraries.jpg",
          width: "1200",
          height: "630"
        }}
      />
      <Navbar darkText />
      
      <div className="pt-20">
        {/* Hero */}
        <section className="relative h-[50vh] flex items-center justify-center">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/de33ba98b41b495607bfae648a4626bb.jpg"
              alt="Antigua Guatemala Itineraries"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          </div>
          <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Antigua Guatemala Itinerary: 2–7 Day Plans</h1>
            <p className="text-xl md:text-2xl">Ready-made trip plans for every schedule and travel style</p>
          </div>
        </section>

        {/* Breadcrumbs */}
        <nav className="bg-gray-50 py-3 px-4">
          <div className="max-w-7xl mx-auto">
            <ol className="flex items-center space-x-2 text-sm">
              <li>
                <Link to="/" className="text-gray-600 hover:text-teal-500 transition">
                  Home
                </Link>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-gray-400">/</span>
                <span className="text-gray-900 font-medium">Itineraries</span>
              </li>
            </ol>
          </div>
        </nav>

        {/* Introduction Content */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Planning your <strong>Antigua Guatemala itinerary</strong> starts with one crucial question: how many days should you spend in this colonial gem? Based on years of helping US travelers plan their trips, we recommend <strong>3 days minimum</strong> for first-time visitors. This gives you enough time to explore the cobblestone streets, complete the iconic Acatenango overnight volcano hike, and experience the city's vibrant culture without feeling rushed.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                That said, Antigua Guatemala works for various trip lengths. A <strong>2-day Antigua itinerary</strong> covers the colonial highlights and one volcano hike—perfect for US travelers on a long weekend. A <strong>5-day Antigua itinerary</strong> allows you to add multiple day trips like Chichicastenango market, coffee tours, and Lake Atitlán. And if you have a full week, a <strong>7-day Antigua Guatemala travel plan</strong> combining Antigua with Lake Atitlán offers the complete Guatemalan highlands experience.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Below, you'll find ready-made <strong>Antigua Guatemala itineraries</strong> for 2, 3, 5, and 7 days, each designed to maximize your time based on your interests and schedule. Whether you're planning an <strong>Antigua weekend itinerary</strong> from the US or a comprehensive <strong>Antigua trip plan</strong>, we've got you covered with detailed day-by-day guides.
              </p>
            </div>
          </div>
        </section>

        {/* Main Itineraries */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">Choose Your Antigua Itinerary</h2>
            <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
              Select the itinerary that matches your available time and travel style
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <Link to="/antigua-guatemala-2-week-itinerary" className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition cursor-pointer">
                <div className="relative h-64">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Antigua%20Guatemala%20weekend%20trip%20Santa%20Catalina%20Arch%20with%20Agua%20volcano%2C%20tourists%20walking%20cobblestone%20streets%2C%20vibrant%20colonial%20buildings%2C%20perfect%20weather%2C%20short%20vacation%20vibe&width=600&height=400&seq=2day-main&orientation=landscape"
                    alt="2-Day Antigua Itinerary"
                    className="w-full h-full object-cover object-center"
                  />
                  <div className="absolute top-4 right-4 bg-teal-500 text-white px-4 py-2 rounded-lg font-bold">
                    2 Days
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-500 transition">Weekend in Antigua</h3>
                  <p className="text-gray-600 mb-4">Perfect for US travelers with limited time. Explore colonial highlights and hike Pacaya volcano in 48 hours.</p>
                  <div className="flex items-center text-teal-500 font-semibold">
                    View Full Itinerary
                    <i className="ri-arrow-right-line ml-2"></i>
                  </div>
                </div>
              </Link>

              <Link to="/antigua-guatemala-3-day-itinerary" className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition cursor-pointer border-2 border-teal-500">
                <div className="relative h-64">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Acatenango%20volcano%20camping%20tents%20at%20sunset%20with%20Fuego%20erupting%2C%20hikers%20enjoying%20view%2C%20adventure%20travel%2C%20overnight%20volcano%20experience%2C%20dramatic%20sky&width=600&height=400&seq=3day-main&orientation=landscape"
                    alt="3-Day Antigua Itinerary"
                    className="w-full h-full object-cover object-center"
                  />
                  <div className="absolute top-4 right-4 bg-teal-500 text-white px-4 py-2 rounded-lg font-bold">
                    3 Days
                  </div>
                  <div className="absolute top-4 left-4 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full font-bold text-xs">
                    MOST POPULAR
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-500 transition">First-Time Visitor Guide</h3>
                  <p className="text-gray-600 mb-4">The ideal introduction with city exploration, Acatenango overnight hike, and Chichicastenango market visit.</p>
                  <div className="flex items-center text-teal-500 font-semibold">
                    View Full Itinerary
                    <i className="ri-arrow-right-line ml-2"></i>
                  </div>
                </div>
              </Link>

              <Link to="/antigua-guatemala-2-week-itinerary" className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition cursor-pointer">
                <div className="relative h-64">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Lake%20Atitlan%20Guatemala%20scenic%20view%20with%20traditional%20boats%2C%20colorful%20villages%2C%20volcanoes%2C%20comprehensive%20travel%20experience%2C%20extended%20vacation%20destination&width=600&height=400&seq=5day-main&orientation=landscape"
                    alt="5-Day Antigua Itinerary"
                    className="w-full h-full object-cover object-center"
                  />
                  <div className="absolute top-4 right-4 bg-teal-500 text-white px-4 py-2 rounded-lg font-bold">
                    5 Days
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-500 transition">Complete Experience</h3>
                  <p className="text-gray-600 mb-4">Comprehensive exploration with multiple day trips, volcano hikes, coffee tours, and Lake Atitlán visit.</p>
                  <div className="flex items-center text-teal-500 font-semibold">
                    View Full Itinerary
                    <i className="ri-arrow-right-line ml-2"></i>
                  </div>
                </div>
              </Link>
            </div>

            {/* Specialized Itineraries */}
            <h3 className="text-3xl font-bold text-gray-900 mb-8">Specialized Itineraries</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <Link to="/antigua-guatemala-itineraries" className="bg-white p-6 rounded-lg hover:shadow-md transition cursor-pointer">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-2xl flex-shrink-0">
                    <i className="ri-calendar-line"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">US Long Weekend Trip</h4>
                    <p className="text-gray-600">Maximize a 3-4 day weekend from the US. Flight timing tips and efficient itinerary for short trips.</p>
                  </div>
                </div>
              </Link>

              <Link to="/antigua-guatemala-itineraries" className="bg-white p-6 rounded-lg hover:shadow-md transition cursor-pointer">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-2xl flex-shrink-0">
                    <i className="ri-route-line"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Antigua + Lake Atitlán Combo</h4>
                    <p className="text-gray-600">7-day itinerary combining Antigua's colonial charm with Lake Atitlán's natural beauty.</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* How Many Days Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">How Many Days Do You Need in Antigua Guatemala?</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                The answer depends on your travel style and what you want to experience. Here's our honest assessment based on helping hundreds of US travelers plan their <strong>Antigua Guatemala trip</strong>:
              </p>

              <div className="bg-teal-50 border-l-4 border-teal-500 p-6 mb-8">
                <p className="text-lg font-semibold text-gray-900 mb-2">Our Recommendation: 3 Days Minimum</p>
                <p className="text-gray-700">
                  Three days gives you enough time to explore the colonial city, complete the spectacular Acatenango overnight volcano hike, and still have energy to enjoy Antigua's restaurants and cafes. This is the sweet spot for first-time visitors.
                </p>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                <strong>Can you visit Antigua in 2 days?</strong> Yes, but you'll need to skip the Acatenango overnight hike and stick to Pacaya volcano instead. A <strong>2-day Antigua itinerary</strong> works for US travelers on a tight schedule, but you'll leave wishing you had more time.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                <strong>Is 5 days too much?</strong> Not at all. A <strong>5-day Antigua itinerary</strong> allows you to explore at a relaxed pace, add multiple day trips (Chichicastenango market, coffee tours, Iximché ruins), and truly immerse yourself in the local culture. You won't feel rushed, and you'll have time to discover hidden gems beyond the main tourist sites.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                <strong>What about a week?</strong> If you have 7 days, we strongly recommend combining Antigua with Lake Atitlán. Spend 3-4 days in Antigua, then 3 days at the lake. This gives you the complete Guatemalan highlands experience—colonial history, volcano adventures, and stunning natural beauty.
              </p>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Antigua Itinerary Comparison</h2>
            
            {/* Mobile scroll hint */}
            <div className="mb-4 text-center md:hidden">
              <p className="text-sm text-gray-600 flex items-center justify-center">
                <i className="ri-arrow-left-right-line mr-2"></i>
                Faites défiler horizontalement pour voir toutes les colonnes
              </p>
            </div>

            <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow-md rounded-lg">
                  <table className="min-w-full bg-white">
                    <thead className="bg-teal-500 text-white">
                      <tr>
                        <th className="px-4 py-4 text-left font-bold text-sm md:text-base whitespace-nowrap">Duration</th>
                        <th className="px-4 py-4 text-left font-bold text-sm md:text-base whitespace-nowrap">Best For</th>
                        <th className="px-4 py-4 text-left font-bold text-sm md:text-base whitespace-nowrap">Includes</th>
                        <th className="px-4 py-4 text-left font-bold text-sm md:text-base whitespace-nowrap">Difficulty</th>
                        <th className="px-4 py-4 text-left font-bold text-sm md:text-base whitespace-nowrap">Recommended For</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-4 font-bold text-gray-900 whitespace-nowrap text-sm md:text-base">2 Days</td>
                        <td className="px-4 py-4 text-gray-700 text-sm md:text-base">Quick weekend trips</td>
                        <td className="px-4 py-4 text-gray-700 text-sm md:text-base">City tour + Pacaya volcano</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs md:text-sm font-semibold">Easy</span>
                        </td>
                        <td className="px-4 py-4 text-gray-700 text-sm md:text-base">US long weekends, tight schedules</td>
                      </tr>
                      <tr className="hover:bg-gray-50 bg-teal-50">
                        <td className="px-4 py-4 font-bold text-gray-900 whitespace-nowrap text-sm md:text-base">3 Days</td>
                        <td className="px-4 py-4 text-gray-700 text-sm md:text-base">First-time visitors</td>
                        <td className="px-4 py-4 text-gray-700 text-sm md:text-base">City + Acatenango overnight hike</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs md:text-sm font-semibold">Moderate</span>
                        </td>
                        <td className="px-4 py-4 text-gray-700 text-sm md:text-base">Most travelers, adventure seekers</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-4 font-bold text-gray-900 whitespace-nowrap text-sm md:text-base">5 Days</td>
                        <td className="px-4 py-4 text-gray-700 text-sm md:text-base">Comprehensive exploration</td>
                        <td className="px-4 py-4 text-gray-700 text-sm md:text-base">Multiple day trips + volcanoes + culture</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs md:text-sm font-semibold">Moderate</span>
                        </td>
                        <td className="px-4 py-4 text-gray-700 text-sm md:text-base">Relaxed pace, culture enthusiasts</td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-4 font-bold text-gray-900 whitespace-nowrap text-sm md:text-base">7 Days</td>
                        <td className="px-4 py-4 text-gray-700 text-sm md:text-base">Antigua + Lake Atitlán</td>
                        <td className="px-4 py-4 text-gray-700 text-sm md:text-base">Complete highlands experience</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="inline-block px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs md:text-sm font-semibold">Challenging</span>
                        </td>
                        <td className="px-4 py-4 text-gray-700 text-sm md:text-base">Full vacation, nature lovers</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Mobile scroll hint bottom */}
            <div className="mt-4 text-center md:hidden">
              <p className="text-xs text-gray-500">
                Tableau optimisé pour le défilement horizontal
              </p>
            </div>
          </div>
        </section>

        {/* Best Itineraries by Travel Style */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Best Antigua Guatemala Itineraries by Travel Style</h2>

            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">For First-Time Visitors</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  If this is your first trip to Antigua Guatemala, follow our <Link to="/antigua-guatemala-3-day-itinerary" className="text-teal-500 hover:underline font-semibold">3-day itinerary</Link>. It covers all the essentials: colonial architecture, the iconic Acatenango overnight volcano hike with views of erupting Fuego, and time to explore the city's markets, cafes, and restaurants. This <strong>3-day Antigua Guatemala itinerary</strong> strikes the perfect balance between adventure and cultural immersion.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">For Adventure Travelers</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Adventure seekers should plan at least 5 days to maximize volcano experiences. Start with the <Link to="/acatenango-hike-from-antigua" className="text-teal-500 hover:underline font-semibold">Acatenango overnight hike</Link>, add Pacaya volcano for lava viewing, and include a day trip to Iximché ruins. A <strong>5-day Antigua itinerary</strong> gives you time to recover between hikes while still exploring the colonial city and its surroundings.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">For Couples</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Couples looking for a romantic getaway should consider a 5-day trip combining adventure with relaxation. Spend your days exploring colonial streets, hiking volcanoes, and touring coffee plantations, then enjoy romantic dinners at rooftop restaurants with volcano views. Add a couples massage at one of Antigua's spas and sunset cocktails at a colonial hotel terrace.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">For Long Weekend Trips from the US</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  US travelers with a long weekend (3-4 days total) should follow our <strong>Antigua weekend itinerary</strong> strategy: fly in Friday evening, spend Saturday and Sunday exploring the city and hiking Pacaya volcano, then fly home Monday. Book your <Link to="/guatemala-city-airport-to-antigua" className="text-teal-500 hover:underline font-semibold">airport transfer</Link> in advance to maximize your limited time. This compressed <strong>2-day Antigua itinerary</strong> works surprisingly well for quick getaways.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Combining Antigua and Lake Atitlán */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Can You Combine Antigua and Lake Atitlán?</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Absolutely—and we highly recommend it if you have 7 days or more. Lake Atitlán is located about 2.5-3 hours from Antigua by shuttle, making it easy to combine both destinations in one trip. Here's how to structure a <strong>7-day Antigua Guatemala travel plan</strong> with Lake Atitlán:
              </p>

              <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Sample 7-Day Antigua + Lake Atitlán Itinerary</h4>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="font-bold text-teal-500 mr-3">Days 1-2:</span>
                    <span>Explore colonial Antigua, walking tours, markets, and ruins</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-bold text-teal-500 mr-3">Days 3-4:</span>
                    <span>Acatenango overnight volcano hike</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-bold text-teal-500 mr-3">Day 5:</span>
                    <span>Travel to Lake Atitlán, settle into your lakeside hotel</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-bold text-teal-500 mr-3">Days 6-7:</span>
                    <span>Explore lake villages (San Pedro, Santiago, San Marcos), kayaking, and relaxation</span>
                  </li>
                </ul>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                This combination gives you the best of both worlds: Antigua's colonial charm and volcano adventures, plus Lake Atitlán's stunning natural beauty and indigenous culture. Most travelers find this 7-day split ideal, though you can adjust based on your interests.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed">
                <strong>Can you visit Lake Atitlán as a day trip from Antigua?</strong> Technically yes, but we don't recommend it. The 5-6 hours of total travel time leaves you with only a few hours at the lake. If you're short on time, prioritize Antigua and save Lake Atitlán for your next Guatemala trip.
              </p>
            </div>
          </div>
        </section>

        {/* Sample 3-Day Overview */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Sample 3-Day Antigua Guatemala Itinerary Overview</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Here's a quick preview of our most popular <strong>3-day Antigua Guatemala itinerary</strong>. This is the perfect introduction for first-time visitors who want to experience the city's highlights and complete the iconic Acatenango volcano hike.
            </p>

            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-teal-500">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Day 1: Colonial Antigua Exploration</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Walking tour of Santa Catalina Arch and Central Park</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Explore local markets and colonial ruins</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-teal-500">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Days 2-3: Acatenango Overnight Volcano Hike</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Hike to base camp and watch Fuego volcano erupt</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 mr-2 mt-1"></i>
                    <span>Sunrise summit hike, then descend to Antigua</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link to="/antigua-guatemala-3-day-itinerary" className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap">
                View Complete 3-Day Itinerary
              </Link>
            </div>
          </div>
        </section>

        {/* Planning Tips */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">How To Choose Your Itinerary</h2>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="text-xl font-bold text-gray-900 mb-3">If you have 2 days...</h4>
                <p className="text-gray-700">Focus on Antigua's colonial center and one volcano hike (Pacaya is easier). Skip day trips and prioritize the city's highlights. This works for US travelers on a long weekend, but you'll miss the spectacular Acatenango experience.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="text-xl font-bold text-gray-900 mb-3">If you have 3 days...</h4>
                <p className="text-gray-700">Add the Acatenango overnight hike for an unforgettable experience. This is the minimum time we recommend for first-time visitors. You'll have one day to explore the city and two days for the volcano adventure.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="text-xl font-bold text-gray-900 mb-3">If you have 5+ days...</h4>
                <p className="text-gray-700">Include multiple <Link to="/day-trips-from-antigua-guatemala" className="text-teal-500 hover:underline font-semibold">day trips</Link> (Chichicastenango market, coffee tours, Iximché ruins) and consider adding Lake Atitlán to your itinerary. This allows for a relaxed pace with time to truly immerse yourself in Guatemalan culture.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
            
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-3">How many days are enough in Antigua Guatemala?</h4>
                <p className="text-gray-700">We recommend 3 days minimum for first-time visitors to Antigua Guatemala. This allows time to explore the colonial city center, complete the iconic Acatenango overnight volcano hike, and take one day trip. If you have 5-7 days, you can add Lake Atitlán, coffee tours, and Chichicastenango market.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Is 2 days enough in Antigua Guatemala?</h4>
                <p className="text-gray-700">Two days is enough to see Antigua's main colonial highlights and hike Pacaya volcano, but you'll miss the spectacular Acatenango overnight experience. A 2-day itinerary works for US travelers on a long weekend, but we recommend 3 days minimum if possible.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Is 3 days too much for Antigua Guatemala?</h4>
                <p className="text-gray-700">No, 3 days is the ideal duration for Antigua Guatemala. This gives you time to explore the city without rushing, complete the Acatenango overnight hike, and still have energy to enjoy the restaurants and cafes. Most first-time visitors find 3 days perfect.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-3">What is the best itinerary for first-time visitors to Antigua?</h4>
                <p className="text-gray-700">The best first-time itinerary is 3 days: Day 1 - Explore colonial Antigua (Santa Catalina Arch, Central Park, ruins, markets). Day 2-3 - Acatenango overnight volcano hike to watch Fuego erupt. This covers the essential experiences without feeling rushed.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Can you visit Lake Atitlán from Antigua Guatemala?</h4>
                <p className="text-gray-700">Yes, Lake Atitlán is a 2.5-3 hour drive from Antigua. You can visit as a long day trip, but we recommend staying 2-3 nights at the lake. A 7-day itinerary combining Antigua (3-4 days) and Lake Atitlán (3 days) is ideal for experiencing both destinations properly.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Is Antigua Guatemala worth 5 days?</h4>
                <p className="text-gray-700">Yes, 5 days in Antigua allows you to explore at a relaxed pace with multiple day trips. You can hike Acatenango, visit Chichicastenango market, tour coffee plantations, explore Iximché ruins, and still have time to enjoy the city's restaurants, cafes, and nightlife without rushing.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-3">What should I prioritize if I have limited time in Antigua?</h4>
                <p className="text-gray-700">With limited time, prioritize: 1) Walking tour of colonial Antigua (Santa Catalina Arch, Central Park, La Merced Church), 2) One volcano hike (Pacaya for 2 days, Acatenango for 3+ days), 3) Sunset at a rooftop bar. Skip distant day trips and focus on the city and one volcano experience.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-3">When is the best time to visit Antigua Guatemala?</h4>
                <p className="text-gray-700">The best time to visit Antigua is during the dry season (November to April), with December-February offering the clearest volcano views. March-April brings Semana Santa (Holy Week) celebrations. The rainy season (May-October) has afternoon showers but fewer crowds and lower prices.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-teal-500">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Plan Your Perfect Antigua Itinerary</h2>
            <p className="text-xl text-white/90 mb-8">Get personalized itinerary recommendations based on your travel dates, interests, and available time. We'll help you create the perfect Antigua Guatemala travel plan.</p>
            <Link to="/plan-my-antigua-trip" className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap">
              Get Custom Travel Plan
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}